/**
 * Every date and time the app shows, pinned to India.
 *
 * The API stores and sends UTC. `toLocaleString()` without a `timeZone` renders in whatever
 * zone the DEVICE is set to — so the same booking read "11:15 am" on a customer's phone (whose
 * screen pinned IST) and something else entirely on a vendor's, and each party was certain the
 * other was wrong.
 *
 * The platform operates in one timezone. A booking is at 11:15 in Bengaluru whether the person
 * reading it is in Bengaluru, on holiday, or has their laptop clock set to New York — so the
 * zone is a property of the business, not of the reader, and is fixed here rather than left to
 * whatever each screen remembered to pass.
 *
 * Use these instead of calling `toLocale*` directly. The one legitimate exception is a duration
 * or a relative age ("3 days ago"), which is a difference between two instants and has no
 * timezone at all.
 */
export const IST = 'Asia/Kolkata';

/** "Thu, 27 Aug, 11:15 am" — the standard stamp for an appointment. */
export function slot_label(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: IST,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * "Thu, 27 Aug, 11:15 am – 12:00 pm" — the whole window.
 *
 * Worth the extra words on any screen where somebody has to physically turn up: a start time
 * alone cannot be checked against the other party's screen, so a mismatch survives being looked
 * at by both of them.
 */
export function slot_range_label(start_iso: string, end_iso: string): string {
  const end = new Date(end_iso).toLocaleTimeString('en-IN', {
    timeZone: IST,
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${slot_label(start_iso)} – ${end}`;
}

/** "27 Aug 2026" — a date with no time attached. */
export function date_label(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** "27 Aug, 11:15 am" — a timestamp on something that is not an appointment. */
export function stamp_label(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "11:15 am" on its own. */
export function time_label(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: IST,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The IST calendar date as YYYY-MM-DD, for comparing "is this today". */
export function ist_date(iso: string | Date): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: IST });
}
